import React, { useEffect, useState } from 'react';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import { IPasswordPolicy } from './IUserProfile.types';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useLayoutContext } from '../../Context/Layout.context';
import { getPasswordPolicy, updateUserPasswordAdminConfirm } from '../../services/users/users.service';
import { useStyles } from './EditUserPassword.styles';
import { AxiosResponse } from 'axios';
import { getErrorMessages, getPasswordPolicyMessage, validatePasswordWithPolicy } from './helpers';
import _ from 'lodash';
import { ActivationAccountResponse } from '../../services/users/users.types';
import { Prompt } from '../Prompt/Prompt.component';
import {
  ErrorAcountConfirmationCode,
  ResponseErrors,
  BackendAccountConfirmationErrorCode,
  BackendAccountConfirmationErrorMessage,
} from '../addUser/AccountError.types';
import { ErrorCode, FrontendResponseErrorCode } from './PasswordError.types';
import { AccountTypeEnum } from '../../models/user.types';
import format from 'date-fns/format';

interface EditUserPasswordByAdminProps {
  userId: string;
  name: string;
  surname: string;
  userEmail: string;
  open: boolean;
  isUserUpdated: () => void;
  onClose?: () => void;
  onNavBack: () => void;
  title?: string;
}

export const getUserRoleDisplayName = (userRole: AccountTypeEnum | null | undefined) => {
  switch (userRole) {
    case AccountTypeEnum.Administrator:
      return 'administratora';
    case AccountTypeEnum.Examiner:
      return 'egzaminatora';
    default:
      return 'osobę o nieznanej roli';
  }
};

export const EditUserPasswordByAdmin: React.FC<EditUserPasswordByAdminProps> = (props) => {
  const classes = useStyles();
  type ErrorAcountAdminConfirmationCode = ErrorAcountConfirmationCode | ErrorCode;

  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<ErrorAcountAdminConfirmationCode[]>([]);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy | undefined>(undefined);

  const [confirmActivationModalOn, setConfirmActivationModalOn] = useState<boolean>(false);
  const [activationAlreadyDoneModalOn, setActivationAlreadyDoneModalOn] = useState<boolean>(false);
  const [activationData, setActivationData] = useState<{
    date: Date | null;
    userRole: string;
  }>({
    date: null,
    userRole: '',
  });

  const { openAlert, setIsLoader } = useLayoutContext();

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowRepeatedPassword = () => {
    setShowRepeatedPassword(!showRepeatedPassword);
  };

  const handleChangeNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleChangeRepeatedPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatedPassword(event.target.value);
    setErrorCodes([]);
  };

  const clearInputs = () => {
    setNewPassword('');
    setRepeatedPassword('');
    setShowNewPassword(false);
    setShowRepeatedPassword(false);
    setErrorCodes([]);
  };

  const handleConfirmAdminUpdate = () => {
    setIsLoader(true);
    updateUserPasswordAdminConfirm(newPassword, props.userId)
      .then(
        (response: AxiosResponse<ActivationAccountResponse>) => {
          if (response.data.identityResult.succeeded) {
            openAlert('success', 'Konto użytkownika zostało aktywowane.');
            props.isUserUpdated();
            clearInputs();
            props.onClose && props.onClose();
            props.onNavBack();
          } else if (response.data.identityResult.errors) {
            const validationErrorCodes = response?.data.identityResult.errors.map(
              (error: ResponseErrors) => error.code,
            );

            const hasEmailAlreadyConfirmed = validationErrorCodes.some(
              (code: string) => code === BackendAccountConfirmationErrorCode.EmailAlreadyConfirmed,
            );
            setErrorCodes(validationErrorCodes);
            if (hasEmailAlreadyConfirmed) {
              clearInputs();
              setConfirmActivationModalOn(false);
              setActivationData({
                date: response.data.confirmedAt ? new Date(response.data.confirmedAt) : null,
                userRole: getUserRoleDisplayName(response.data.confirmedBy),
              });
              setActivationAlreadyDoneModalOn(true);
            } else {
              setConfirmActivationModalOn(false);
              openAlert('error', 'Wystąpił błąd podczas aktywacji konta.');
            }
          }
        },
        () => {
          openAlert('error', 'Wystąpił błąd podczas aktualizacji hasła.');
          clearInputs();
          props.onClose && props.onClose();
          props.onNavBack();
        },
      )
      .finally(() => {
        setIsLoader(false);
      });
  };

  useEffect(() => {
    const dataChanged = newPassword !== '' && repeatedPassword !== '';

    const newErrorCodes: ErrorAcountConfirmationCode[] = [];

    if (newPassword !== '' && repeatedPassword !== '' && newPassword !== repeatedPassword) {
      newErrorCodes.push(FrontendResponseErrorCode.PasswordsNotTheSame);
    }

    if (passwordPolicy && newPassword !== '' && repeatedPassword !== '' && newPassword === repeatedPassword) {
      if (!validatePasswordWithPolicy(newPassword, passwordPolicy)) {
        newErrorCodes.push(FrontendResponseErrorCode.PasswordIsInvalid);
      }
    }

    setErrorCodes(newErrorCodes);

    setIsUpdateButtonDisabled(
      newPassword === '' || repeatedPassword === '' || !dataChanged || newErrorCodes.length > 0,
    );
  }, [newPassword, repeatedPassword, passwordPolicy]);

  const onCancelEdit = () => {
    clearInputs();
    props.onClose && props.onClose();
    props.onNavBack();
  };

  const getAdminErrorMessages = (errorCode: ErrorAcountAdminConfirmationCode) => {
    switch (errorCode) {
      case BackendAccountConfirmationErrorCode.WrongUserId:
        return BackendAccountConfirmationErrorMessage.WrongUserId;
      case BackendAccountConfirmationErrorCode.EmailAlreadyConfirmed:
        return BackendAccountConfirmationErrorMessage.EmailAlreadyConfirmed;
      case BackendAccountConfirmationErrorCode.ExpiredAccount:
        return BackendAccountConfirmationErrorMessage.ExpiredAccountMsgForAdmin;
      default:
        return getErrorMessages(errorCode as ErrorCode);
    }
  };

  useEffect(() => {
    getPasswordPolicy().then((response: AxiosResponse<IPasswordPolicy>) => {
      setPasswordPolicy(response?.data);
    });
  }, []);

  return (
    <>
      <ModalWithButtons
        open={props.open}
        size="sm"
        xCloseButton={onCancelEdit}
        autoHeight={true}
        onCancel={onCancelEdit}
        onConfirm={() => setConfirmActivationModalOn(true)}
        confirmButtonLabel={'Zapisz'}
        confirmButtonDisabled={isUpdateButtonDisabled}
        buttonsContainerStyle={{ marginTop: '20px' }}
        annotations={
          <>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.footer}>{passwordPolicy && getPasswordPolicyMessage(passwordPolicy)}</div>
            </Grid>
          </>
        }
      >
        <Grid container className={classes.container}>
          <Grid>
            <div className={classes.modalSubTitle} style={{ marginTop: props.title ? '30px' : '' }}>
              {props.title ? props.title : 'Aktywuj ręcznie konto użytkownika'}
            </div>
          </Grid>
          <Grid item xs={7} className={classes.item}>
            <div className={classes.emailHeader}>
              Imię i nazwisko: <div className={classes.email}> {props.name + ' ' + props.surname}</div>
            </div>
          </Grid>
          <Grid item xs={7} className={classes.item}>
            <div className={classes.emailHeader}>
              Email: <div className={classes.email}> {props.userEmail}</div>
            </div>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: errorCodes.length > 0 ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="new-password"
              >
                Nowe hasło
              </InputLabel>
              <OutlinedInput
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleChangeNewPassword}
                fullWidth={true}
                error={errorCodes.length > 0}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowNewPassword}
                      edge="end"
                    >
                      {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: errorCodes.length > 0 ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Nowe hasło"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: errorCodes.length > 0 ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="repeted-password"
              >
                Powtórz nowe hasło
              </InputLabel>
              <OutlinedInput
                id="repeted-password"
                type={showRepeatedPassword ? 'text' : 'password'}
                value={repeatedPassword}
                onChange={handleChangeRepeatedPassword}
                fullWidth={true}
                error={errorCodes.length > 0}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowRepeatedPassword}
                      edge="end"
                    >
                      {!showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: errorCodes.length > 0 ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Powtórz nowe hasło"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="password-error" component={'div'}>
                {errorCodes.map((code: ErrorAcountAdminConfirmationCode) => (
                  <div className={classes.errorMessage} key={code}>
                    {getAdminErrorMessages(code)}
                  </div>
                ))}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>
      <Prompt
        type="confirmation"
        mainActionButtonText="Tak"
        open={confirmActivationModalOn}
        mainText="Nie będzie możliwe przywrócenie tej operacji. Czy na pewno chcesz ręcznie aktywować konto użytkownika?"
        onConfirm={handleConfirmAdminUpdate}
        onCancel={() => setConfirmActivationModalOn(false)}
      />
      <Prompt
        type="info"
        mainActionButtonText="Zamknij"
        open={activationAlreadyDoneModalOn}
        mainText={`Aktywacja konta została wykonana w dniu ${
          activationData.date ? format(activationData.date, 'dd.MM.yyyy, HH:mm') : 'nieznana data'
        } przez ${activationData.userRole}.`}
        onConfirm={() => {
          onCancelEdit();
          props.isUserUpdated();
        }}
      />
    </>
  );
};
